import { BillingAddonReferenceFragment, GetDatoModulesQuery } from 'generated/resources/graphql'

import { ModuleStatus } from 'modules/marketplace_modules/lib/getModuleStatus'
import { BUSINESS_VARIANT } from 'modules/upsell/lib/selfUpsell/plans/utils'

type ModulesDatoModule = NonNullable<
  GetDatoModulesQuery['modules']['datoModulesConnection']['nodes'][number]
>

export interface ButtonVisibilityParams {
  isInstalled: boolean
  variant: ModuleStatus['variant']
  moduleStatus: ModuleStatus
  hasEnterpriseVariant: boolean
  canSeeSelfUpsell: boolean
  canSeeNormalUpsell: boolean
  ignoreUpsell: boolean
  isLargeSegment: boolean
  hasMeetingLink?: boolean
  addonReference: BillingAddonReferenceFragment[]
  moduleData?: ModulesDatoModule | null
}

export interface ButtonVisibilityResult {
  showSelfUpsellButton: boolean
  shouldShowUpgradeButton: boolean
  shouldShowBookAMeetingButton: boolean
  shouldShowRequestUpsellButtonPrimary: boolean
  shouldShowRequestUpsellButtonSecondary: boolean
}

const isRecruitment = (addonReference: BillingAddonReferenceFragment[]): boolean => {
  return addonReference.some((addon) => addon.internalTitle.toLowerCase().includes('recruitment'))
}

const isExpenses = (addonReference: BillingAddonReferenceFragment[]): boolean => {
  return addonReference.some((addon) => addon.internalTitle.toLowerCase().includes('expenses'))
}

export const getButtonVisibility = (params: ButtonVisibilityParams): ButtonVisibilityResult => {
  const {
    isInstalled,
    variant,
    moduleStatus,
    hasEnterpriseVariant,
    canSeeSelfUpsell,
    canSeeNormalUpsell,
    ignoreUpsell,
    isLargeSegment,
    hasMeetingLink = false,
    addonReference,
    moduleData,
  } = params

  const disableSelfUpsell = Boolean(moduleData?.disableSelfUpsell)
  const meetingOnlyFlow = disableSelfUpsell && !!canSeeNormalUpsell && !isInstalled

  const showSelfUpsellButton =
    (canSeeSelfUpsell || ignoreUpsell) && !isInstalled && !disableSelfUpsell

  const shouldShowUpgradeButton =
    isInstalled &&
    variant === BUSINESS_VARIANT &&
    canSeeSelfUpsell &&
    hasEnterpriseVariant &&
    !isRecruitment(addonReference) &&
    !isExpenses(addonReference) &&
    moduleStatus.from !== 'bundle'

  const shouldShowBookAMeetingButton =
    (isLargeSegment && hasMeetingLink && !isInstalled) || (meetingOnlyFlow && hasMeetingLink)

  const shouldShowRequestUpsellButtonPrimary =
    (canSeeNormalUpsell && !isInstalled && !disableSelfUpsell && !showSelfUpsellButton && !shouldShowUpgradeButton) ||
    meetingOnlyFlow

  const shouldShowRequestUpsellButtonSecondary =
    (canSeeNormalUpsell &&
      !isInstalled &&
      isLargeSegment &&
      !shouldShowRequestUpsellButtonPrimary &&
      !disableSelfUpsell) ||
    meetingOnlyFlow

  // TEMPORARY: Always show "request a meeting" instead of "book a meeting"
  return {
    showSelfUpsellButton,
    shouldShowUpgradeButton,
    shouldShowBookAMeetingButton: false,
    shouldShowRequestUpsellButtonPrimary,
    shouldShowRequestUpsellButtonSecondary: shouldShowRequestUpsellButtonSecondary || shouldShowBookAMeetingButton,
  }
}
